// Moving a scheduled post to another day on the calendar. Pure rules, no React.
import type { PostDto } from '@str/api/types';
import { DAYS } from './format';
import { isDayOver } from './schedule';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

type MovePost = Pick<PostDto, 'id' | 'status' | 'imported' | 'channels' | 'scheduledDate'>;

/** Only a post still waiting to go out can move; what went out, failed or was imported stays. */
export function canMove(p: MovePost): boolean {
  return p.status === 'scheduled' && !p.imported;
}

export type DropCheck =
  { ok: true } | { ok: false; why: 'In the past' | 'Same day' | 'Already has a post' };

/**
 * A day can take the post when it is today or later, not the day it is already on, and no other
 * post still waiting to go out uses one of the same channels that day. With the post's zone,
 * today counts until its last minute goes by on that clock: any later time can still be picked.
 */
export function canDropOn(
  iso: string,
  p: MovePost,
  todayIso: string,
  others: MovePost[],
  clock?: { zone: string; now?: Date },
): DropCheck {
  if (iso < todayIso) return { ok: false, why: 'In the past' };
  if (clock && isDayOver(iso, clock.zone, clock.now)) return { ok: false, why: 'In the past' };
  if (iso === p.scheduledDate) return { ok: false, why: 'Same day' };
  const clash = others.some(
    (o) =>
      o.id !== p.id &&
      o.scheduledDate === iso &&
      o.status === 'scheduled' &&
      o.channels.some((c) => p.channels.includes(c)),
  );
  if (clash) return { ok: false, why: 'Already has a post' };
  return { ok: true };
}

export function dayWithMonth(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return `${DAYS[(d.getDay() + 6) % 7]} ${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

export function movedLabel(iso: string, time: string): string {
  return `Moved to ${dayWithMonth(iso)}, ${time}`;
}
